import React from 'react';
import { Eye } from 'lucide-react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

const ChatGrid = ({ chats, onChatClick, onNewChat, currentContext, isMobile = false, selectedGroup = null }) => {
  console.log('🔵 ChatGrid rendering with chats:', chats?.length || 0);

  const themeConfig = useTheme();
  const { t } = useTranslation('common');
  const { requireAuth } = useAuth();

  // Get original chats (threads) from mockData
  const chatThreads = (chats || []).filter(chat => !chat.parent_chat_id);

  // Desktop Chat Row - simplified: headline + user name + Open button
  const ChatRow = ({ chat, onClick }) => {
    return (
      <div
        onClick={() => onClick(chat)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '2px 16px',
          cursor: 'pointer',
          borderBottom: '1px solid #f0f0f0',
          transition: 'background-color 0.15s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        {/* Headline */}
        <div style={{
          flex: 1,
          fontFamily: 'OpenSauceSans, Arial, sans-serif',
          fontSize: '14px',
          fontWeight: '500',
          color: '#1f2937',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          marginRight: '16px'
        }}>
          {chat.headline || chat.content}
        </div>

        {/* Author name - context colored, greyed if removed */}
        <span style={{
          fontFamily: 'OpenSauceSans, Arial, sans-serif',
          fontSize: '13px',
          fontWeight: '600',
          color: chat.is_author_removed ? '#9CA3AF' : themeConfig.primary,
          marginRight: '16px',
          whiteSpace: 'nowrap',
          fontStyle: chat.is_author_removed ? 'italic' : 'normal'
        }}>
          {chat.is_author_removed ? t('removed_author') : (chat.author_name || `User ${chat.author_id}`)}
        </span>

        {/* View count */}
        {chat.view_count > 0 && (
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontFamily: 'OpenSauceSans, Arial, sans-serif',
            fontSize: '12px',
            fontWeight: '500',
            color: '#64748B',
            marginRight: '16px',
            whiteSpace: 'nowrap'
          }}>
            <Eye size={14} />
            {chat.view_count}
          </span>
        )}

        {/* Open button */}
        <button
          style={{
            fontFamily: 'OpenSauceSans, Arial, sans-serif',
            fontSize: '12px',
            fontWeight: '600',
            backgroundColor: '#10B981',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '3px 14px',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#059669';
            e.stopPropagation();
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#10B981';
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            onClick(chat);
          }}
        >
          {t('open')}
        </button>
      </div>
    );
  };

  const handleCardClick = (chat) => {
    console.log('ChatGrid handleCardClick called with chat:', chat);
    if (onChatClick) {
      onChatClick(chat);
    }
  };

  const handleCreateClick = () => {
    console.log('Create new chat button clicked');
    requireAuth('gate_body_create_chat', () => {
      window.dispatchEvent(new CustomEvent('ournet:navigate', {
        detail: {
          view: 'new-chat',
          mode: 'add',
          groupId: selectedGroup?.id,
          groupName: selectedGroup?.name
        }
      }));
    });
  };

  // Mobile Chat Row Component - simplified
  const MobileChatRow = ({ chat, onClick }) => {
    return (
      <div
        onClick={() => onClick(chat)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '2px 0',
          cursor: 'pointer',
          borderBottom: '1px solid #e5e7eb'
        }}
      >
        {/* Headline + Author */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: '500',
            fontSize: '14px',
            color: '#1f2937',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginBottom: '1px'
          }}>
            {chat.headline || chat.content}
          </div>
          <span style={{
            fontSize: '12px',
            fontWeight: '600',
            color: chat.is_author_removed ? '#9CA3AF' : themeConfig.primary,
            fontStyle: chat.is_author_removed ? 'italic' : 'normal'
          }}>
            {chat.is_author_removed ? t('removed_author') : (chat.author_name || `User ${chat.author_id}`)}
          </span>
        </div>

        {/* Open button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick(chat);
          }}
          style={{
            backgroundColor: '#10B981',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '3px 12px',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            marginLeft: '12px',
            flexShrink: 0
          }}
        >
          {t('open')}
        </button>
      </div>
    );
  };

  // Mobile Layout
  // Bottom padding ensures content is visible above floating footer buttons
  if (isMobile) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        padding: '8px 16px',
        paddingBottom: '80px', // Space for floating footer buttons
        overflowY: 'auto',
        backgroundColor: 'white',
        borderRadius: '12px',
        margin: '8px'
      }}>
        {/* Group image at top of chat list on mobile */}
        {isMobile && selectedGroup?.logo_url && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '8px'
          }}>
            <div style={{
              width: '160px',
              aspectRatio: '1 / 1',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
            }}>
              <img
                src={selectedGroup.logo_url}
                alt={selectedGroup.name}
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
          </div>
        )}
        {chatThreads.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#6b7280',
            fontSize: '14px'
          }}>
            {t('no_chat_threads_yet')}
          </div>
        ) : (
          chatThreads.map(chat => (
            <MobileChatRow
              key={chat.id}
              chat={chat}
              onClick={handleCardClick}
            />
          ))
        )}
      </div>
    );
  }

  // Desktop Layout
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'transparent',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* White container - 750px wide, 80px from top, aligned to left */}
      <div
        style={{
          position: 'absolute',
          top: '0',
          left: '0',
          width: '750px',
          maxWidth: 'calc(100% - 520px)',
          maxHeight: 'calc(100vh - 200px)',
          backgroundColor: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Scrollable content area */}
        <div style={{
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          padding: '8px 0'
        }}>
          {chatThreads.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 40px',
              color: '#6b7280',
              fontSize: '15px'
            }}>
              {t('no_chat_threads_yet')}
            </div>
          ) : (
            chatThreads.map(chat => (
              <ChatRow
                key={chat.id}
                chat={chat}
                onClick={handleCardClick}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatGrid;
